/**
 * Shared plan-price formatter (issue #607 Contracts pinned "Shared
 * formatter"). Every plan-price display on `/new-plan`, `/payment-details`
 * and `/pricing` (and the upgrade/downgrade dialogs) goes through this pure
 * function so the same dollar amount never renders with a different number
 * of decimals depending on which component prints it — the bug was
 * `/new-plan`'s "Plan Type" line printing "$248.7" beside a "Due today" line
 * printing "$248.70" for the exact same price.
 *
 * Returns `Number(dollars).toFixed(2)` — the same rounding every
 * already-correct call site used (`/pricing`, `applyDiscount()` in
 * `coupon-price.utils.ts`, #601's `centsToDollars()`). Returns `""` for
 * `null`, `undefined`, or anything that does not parse to a finite number
 * (an unpriced/missing identifier); the caller decides whether to render a
 * `$` at all in that case — this function never invents a price.
 *
 * Deliberately does **not** use `Math.trunc`: that was the source of issue
 * #607 (a price like `248.7` losing its trailing zero on one line while a
 * sibling line correctly rounded it), and it disagrees with how every other
 * call site on these pages already rounds.
 */
export function formatPrice(dollars: number | string | null | undefined): string {
  if (dollars === null || dollars === undefined) {
    return "";
  }
  const value = Number(dollars);
  return Number.isFinite(value) ? value.toFixed(2) : "";
}

/**
 * `formatPrice` for a cents amount — the API's `unit_amount` shape
 * (`src/app/core/models/stripe.models.ts:29`). `formatCents(24870) ===
 * "248.70"`.
 */
export function formatCents(cents: number | string | null | undefined): string {
  if (cents === null || cents === undefined) {
    return "";
  }
  const value = Number(cents);
  return Number.isFinite(value) ? formatPrice(value / 100) : "";
}
